/**
 * @fileoverview Image Set Selector Component
 * @description Choose between Family photos or Random images for memory game
 */

import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ImageSetType } from "../types";

interface ImageSetSelectorProps {
  selectedType: ImageSetType;
  onSelectType: (type: ImageSetType) => void;
  familyImagesAvailable: boolean;
  familyImageCount?: number;
}

/**
 * ImageSetSelector - Choose image set for memory game
 */
export const ImageSetSelector: React.FC<ImageSetSelectorProps> = ({
  selectedType,
  onSelectType,
  familyImagesAvailable,
  familyImageCount = 0,
}) => {
  return (
    <View style={styles.container}>
      <Text style={styles.title}>Kies afbeeldingen</Text>

      <View style={styles.options}>
        {/* Random Images */}
        <TouchableOpacity
          style={[
            styles.option,
            selectedType === "random" && styles.optionSelected,
          ]}
          onPress={() => onSelectType("random")}
          accessibilityLabel="Willekeurige afbeeldingen"
          accessibilityRole="radio"
          accessibilityState={{ checked: selectedType === "random" }}
        >
          <View
            style={[
              styles.iconContainer,
              selectedType === "random" && styles.iconContainerSelected,
            ]}
          >
            <Ionicons
              name="images"
              size={28}
              color={selectedType === "random" ? "#FFFFFF" : "#8B5CF6"}
            />
          </View>
          <Text
            style={[
              styles.optionTitle,
              selectedType === "random" && styles.optionTitleSelected,
            ]}
          >
            Willekeurig
          </Text>
          <Text style={styles.optionSubtitle}>Leuke plaatjes</Text>

          {/* Preview thumbnails */}
          <View style={styles.previewRow}>
            <Image
              source={require("@/assets/memory_game_images/robot.jpg")}
              style={styles.previewImage}
            />
            <Image
              source={require("@/assets/memory_game_images/spiderman.jpg")}
              style={styles.previewImage}
            />
            <Image
              source={require("@/assets/memory_game_images/minion_hero.jpg")}
              style={styles.previewImage}
            />
          </View>

          {selectedType === "random" && (
            <View style={styles.checkmark}>
              <Ionicons name="checkmark-circle" size={24} color="#22C55E" />
            </View>
          )}
        </TouchableOpacity>

        {/* Family Images */}
        <TouchableOpacity
          style={[
            styles.option,
            selectedType === "family" && styles.optionSelected,
            !familyImagesAvailable && styles.optionDisabled,
          ]}
          onPress={() => familyImagesAvailable && onSelectType("family")}
          disabled={!familyImagesAvailable}
          accessibilityLabel={`Familie foto's${
            !familyImagesAvailable ? ", niet beschikbaar" : ""
          }`}
          accessibilityRole="radio"
          accessibilityState={{
            checked: selectedType === "family",
            disabled: !familyImagesAvailable,
          }}
        >
          <View
            style={[
              styles.iconContainer,
              selectedType === "family" && styles.iconContainerFamily,
              !familyImagesAvailable && styles.iconContainerDisabled,
            ]}
          >
            <Ionicons
              name="people"
              size={28}
              color={
                !familyImagesAvailable
                  ? "#6B7280"
                  : selectedType === "family"
                  ? "#FFFFFF"
                  : "#EC4899"
              }
            />
          </View>
          <Text
            style={[
              styles.optionTitle,
              selectedType === "family" && styles.optionTitleSelected,
              !familyImagesAvailable && styles.optionTitleDisabled,
            ]}
          >
            Familie
          </Text>
          <Text
            style={[
              styles.optionSubtitle,
              !familyImagesAvailable && styles.optionSubtitleDisabled,
            ]}
          >
            {familyImagesAvailable
              ? `${familyImageCount} foto's`
              : "Nog geen foto's"}
          </Text>

          {/* Placeholder for family photos */}
          <View style={styles.previewRow}>
            <View style={[styles.previewPlaceholder, !familyImagesAvailable && styles.previewPlaceholderDisabled]}>
              <Ionicons name="image" size={16} color="#9CA3AF" />
            </View>
            <View style={[styles.previewPlaceholder, !familyImagesAvailable && styles.previewPlaceholderDisabled]}>
              <Ionicons name="image" size={16} color="#9CA3AF" />
            </View>
            <View style={[styles.previewPlaceholder, !familyImagesAvailable && styles.previewPlaceholderDisabled]}>
              <Ionicons name="image" size={16} color="#9CA3AF" />
            </View>
          </View>

          {selectedType === "family" && familyImagesAvailable && (
            <View style={styles.checkmark}>
              <Ionicons name="checkmark-circle" size={24} color="#22C55E" />
            </View>
          )}
        </TouchableOpacity>
      </View>

      {!familyImagesAvailable && (
        <View style={styles.infoBox}>
          <Ionicons name="information-circle" size={16} color="#F59E0B" />
          <Text style={styles.infoText}>
            Vraag een admin om familie foto's toe te voegen
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#E5E7EB",
    marginBottom: 12,
  },
  options: {
    flexDirection: "row",
    gap: 12,
  },
  option: {
    flex: 1,
    backgroundColor: "#1E293B",
    borderRadius: 16,
    padding: 16,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "transparent",
    position: "relative",
  },
  optionSelected: {
    borderColor: "#8B5CF6",
    backgroundColor: "rgba(139, 92, 246, 0.1)",
  },
  optionDisabled: {
    opacity: 0.6,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "rgba(139, 92, 246, 0.15)",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  iconContainerSelected: {
    backgroundColor: "#8B5CF6",
  },
  iconContainerFamily: {
    backgroundColor: "#EC4899",
  },
  iconContainerDisabled: {
    backgroundColor: "rgba(107, 114, 128, 0.2)",
  },
  optionTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#E5E7EB",
    marginBottom: 2,
  },
  optionTitleSelected: {
    color: "#FFFFFF",
  },
  optionTitleDisabled: {
    color: "#6B7280",
  },
  optionSubtitle: {
    fontSize: 12,
    color: "#9CA3AF",
    marginBottom: 8,
  },
  optionSubtitleDisabled: {
    color: "#6B7280",
  },
  previewRow: {
    flexDirection: "row",
    gap: 4,
    marginTop: 4,
  },
  previewImage: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  previewPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: "#374151",
    justifyContent: "center",
    alignItems: "center",
  },
  previewPlaceholderDisabled: {
    backgroundColor: "#1F2937",
  },
  checkmark: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    padding: 10,
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    borderRadius: 8,
  },
  infoText: {
    fontSize: 12,
    color: "#F59E0B",
    flex: 1,
  },
});

export default ImageSetSelector;

